/**
 * URL redaction for LOGS-02 hard gate.
 *
 * Rules (D-13):
 * 1. Strip the entire query string (everything after `?`)
 * 2. Replace path segments that look like UUIDs, numeric IDs, or token-like strings with `[id]`
 *
 * The output MUST NEVER contain: Bearer, eyJ (JWT prefix), password, token=, ?, secret.
 * This is enforced by the redact.test.ts hard-gate test suite.
 */

/** UUID v4/v5 pattern */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Pure numeric ID */
const NUMERIC_RE = /^\d+$/;

/**
 * Token-like segment: long (>= 16 chars) alphanumeric / base64url / hex strings
 * that don't look like human-readable resource names.
 * Catches JWT fragments (eyJ...), hex keys, base64 tokens, etc.
 */
const TOKEN_LIKE_RE = /^[A-Za-z0-9+/=_-]{16,}$/;

/**
 * Redact a URL before storing it in the ring buffer.
 *
 * @param raw - The raw URL captured from fetch instrumentation.
 * @returns A safe, redacted URL with no query string and no id-like path segments.
 */
export function redactUrl(raw: string): string {
    // Step 1: strip the query string entirely
    const withoutQuery = raw.split('?')[0];

    // Step 2: redact id-like path segments
    const parts = withoutQuery.split('/');
    const redacted = parts.map((segment) => {
        if (segment === '') return segment; // preserve leading slash / empty parts
        if (UUID_RE.test(segment)) return '[id]';
        if (NUMERIC_RE.test(segment)) return '[id]';
        if (TOKEN_LIKE_RE.test(segment)) return '[id]';
        return segment;
    });

    return redacted.join('/');
}
